import { ViewContainerRef } from '@angular/core';
import { AbcCommentsListComponent, DocumentsEditorCardComponent, PropertyType } from '@barthauer/abc/common-editors';
import { CompareHelper, StringHelper } from '@barthauer/abc/core';
import { ObjectTypeInfoViewModel } from '@barthauer/abc/core/data';
import { AbcCameraDetailsService, GalleryModel } from '@barthauer/abc/ui-components';
import { Item } from 'devextreme/ui/accordion';
import { Item as TabPanelItem } from 'devextreme/ui/tab_panel';
import { ArenaUIConfiguration, DataFormStyle } from 'libs/abc/arena/src/lib/abc-arena.service';
import { AbcAccordionUiCreator } from 'libs/abc/arena/src/lib/core/data-form/core/ui/abc-accordion-ui-creator';
import { AbcGalleryUiCreator } from 'libs/abc/arena/src/lib/core/data-form/core/ui/abc-gallery-ui-creator';
import { DateBoxType } from 'libs/abc/ui-components/src/lib/core/src/abc-date-box/abc-date-box.component';
import { ArenaService } from '../../arena.service';
import {
  FormatSettings,
  LabelLayoutSettings,
  LayoutColumnType,
  LayoutGroupType,
  LayoutSettings,
  SizeType,
  SpecialType,
  TableLayoutSettings,
} from '../../models/layout.model';
import { AbcComponent } from '../../shared/type';
import {
  LayoutColumnViewModel,
  LayoutGroupViewModel,
  LayoutViewModel,
  SubLayoutViewModel,
} from '../../viewModels/layout.viewModel';
import { PDC } from '../data-form.component';
import { DataFormService } from '../data-form.service';
import { DataFormSettings, UIComponent } from './settings.model';
import { AbcTabsUiCreator } from './ui/abc-tabs-ui-creator';
import { GridOnFormUiCreator } from './ui/grid-on-form-ui-creator';
import { SpecialTypeUiCreator } from './ui/special-type-ui-creator';
import { StandardUiCreator } from './ui/standard-ui-creator';
import { UiCreatorBase } from './ui/ui-creator-base';

export class UiComponentCreator extends UiCreatorBase {
  private gridOnFormCreator: GridOnFormUiCreator;
  private specialTypeCreator: SpecialTypeUiCreator;
  private standardUiCreator: StandardUiCreator;
  public abcComponentUiCreator: AbcGalleryUiCreator | AbcAccordionUiCreator | AbcTabsUiCreator;

  constructor(
    private service: DataFormService,
    private settings: DataFormSettings,
    private abcCameraDetailsService: AbcCameraDetailsService,
    private arenaService: ArenaService,
  ) {
    super(service, settings);
    this.init(service);
  }

  private init(service: DataFormService) {
    this.gridOnFormCreator = new GridOnFormUiCreator(service, this.settings, this.arenaService);
    this.specialTypeCreator = new SpecialTypeUiCreator(service, this.settings, this.abcCameraDetailsService);
    this.standardUiCreator = new StandardUiCreator(service, this.settings);
    if (this.service.arena.uiconfig.dataFormStyle === DataFormStyle.GALLERY) {
      this.abcComponentUiCreator = new AbcGalleryUiCreator(service, this.settings);
    } else if (this.service.arena.uiconfig.dataFormStyle === DataFormStyle.TABS) {
      this.abcComponentUiCreator = new AbcTabsUiCreator(service, this.settings);
    } else {
      this.abcComponentUiCreator = new AbcAccordionUiCreator(service, this.settings);
    }
    const tabs = document.getElementById('tabs');
    if (tabs) {
      tabs.innerHTML = '';
    }
  }

  public async createUiComponents(): Promise<void> {
    return await this.createControls();
  }

  private hasSubLayoutViewsOrColumns(groups: LayoutGroupViewModel[]): boolean {
    if (groups.length === 1) {
      return groups[0]?.subLayoutViews?.length > 0 || groups[0]?.columns?.length > 0;
    }
    return true;
  }

  private async createControls(): Promise<void> {
    await this.createDataFormMainGroupControls(this.settings.layoutViewModel);
    const groups = this.settings.layoutViewModel.groups;
    const hasData = this.hasSubLayoutViewsOrColumns(groups);

    const tabs = document.getElementById('tabs');

    if (tabs) {
      while (tabs.firstChild) {
        tabs.firstChild.remove();
      }

      await this.createDataFormTabsControls(hasData ? groups : null);
      const tabsHTMLElement: HTMLElement = await this.abcComponentUiCreator.create();
      tabs.appendChild(tabsHTMLElement);

      if (this.abcComponentUiCreator instanceof AbcAccordionUiCreator) {
        const dataFormTabs = document.querySelector('.dataFormTabs') as HTMLElement;
        dataFormTabs && (dataFormTabs.style.backgroundColor = 'transparent');
      }
    }
  }

  private areGroupsTabControlOnly(): boolean {
    return this.settings.layoutViewModel.groups.find((group) => group.type === LayoutGroupType.TabPage) ? false : true;
  }

  private async createDataFormMainGroupControls(layoutViewModel: LayoutViewModel): Promise<HTMLTableElement> {
    const table: HTMLTableElement = document.getElementById('mainGroupTable') as HTMLTableElement;
    if (table?.children?.length > 0) {
      table.innerHTML = '';
    }

    table.style.borderSpacing = '0px';
    table.style.tableLayout = 'fixed';

    const layoutSettings = layoutViewModel.mainGroupTableLayoutSettings;

    if (table && layoutSettings?.Columns?.length) {
      this.createTableStructure(table, layoutSettings);

      await this.createControlsAndAppendToTable(
        table,
        -1,
        layoutViewModel.columns,
        layoutViewModel.subLayoutViews,
        layoutViewModel.groups,
      );

      table.tBodies[0]?.insertAdjacentElement('afterbegin', this.createEmptyRowWithColumns(layoutSettings?.Columns));

      if (this.service.config.configuration.Style?.DataForm?.MaxHeightOfEmptyRows?.length > -1) {
        this.changeDefaultCellsHeight(table);
      }
    }
    return table;
  }

  private async createDataFormTabsControls(groups: LayoutGroupViewModel[]): Promise<void> {
    this.settings.groupList = [];
    const dataFormContainer: HTMLElement = document.querySelector('#dataFormComponent');
    const fixedGroupElement: HTMLElement = document.getElementById('fixedGroup');
    const tabsElement: HTMLElement = document.getElementById('tabs');
    let parentContainer: HTMLTableElement;

    dataFormContainer?.style.setProperty('overflow', 'unset');
    fixedGroupElement.style.setProperty('overflow', 'auto');

    if (groups?.length) {
      this.setGroupDefaultCSSValues(fixedGroupElement, tabsElement);

      for (let index = 0; index < groups.length; index++) {
        parentContainer = await this.createGroup(groups, index, parentContainer, tabsElement);
      }
    } else if (!this.settings.isPDCChecked) {
      fixedGroupElement.style.setProperty('height', 'auto', 'important');
      fixedGroupElement.style.setProperty('max-height', '100%', 'important');
      tabsElement.style.display = 'none';
    }

    this.handlePDCComponents();
    this.handleUIConfiguration(tabsElement);
  }

  private setGroupDefaultCSSValues(fixedGroupElement: HTMLElement, tabsElement: HTMLElement): void {
    fixedGroupElement.style.setProperty('height', 'auto', 'important');
    // Commented because the height of the FixedGroup should be static
    // fixedGroupElement.style.setProperty('max-height', '30%', 'important');
    // fixedGroupElement.style.setProperty('min-height', '15%', 'important');
    /////
    fixedGroupElement.style.overflow = 'inherit';

    tabsElement.style.visibility = 'visible';
    tabsElement.style.setProperty('height', '70%', 'important');
    tabsElement.style.display = 'flex';
    tabsElement.style.removeProperty('padding');
    tabsElement.style.removeProperty('background-color');
  }

  private async createGroup(
    groups: LayoutGroupViewModel[],
    groupIndex: number,
    parentContainer: HTMLTableElement,
    tabsElement: HTMLElement,
  ): Promise<HTMLTableElement> {
    const group = groups[groupIndex];
    const nextGroup = groups[groupIndex + 1] ?? undefined;

    let tabsControlsContainer: HTMLElement;

    let tableInsideTabs = this.createTable(group.caption);

    if (parentContainer) {
      tableInsideTabs = parentContainer;
    } else {
      tableInsideTabs = this.createTableStructure(
        tableInsideTabs,
        group.tableLayoutSettings ?? this.settings.layoutViewModel.mainGroupTableLayoutSettings,
      );
    }

    if (group.layoutSettings) {
      tabsControlsContainer = this.createLayoutStructure(tableInsideTabs, group.layoutSettings);
    }

    tableInsideTabs = await this.createControlsAndAppendToTable(
      tableInsideTabs,
      groupIndex,
      group.columns,
      group.subLayoutViews,
    );

    if (this.service.config.configuration.Style?.DataForm?.MaxHeightOfEmptyRows?.length > -1) {
      this.changeDefaultCellsHeight(tableInsideTabs);
    }

    if (group.groups.length && this.service.arena.uiconfig.dataFormStyle === DataFormStyle.ACCORDION) {
      await this.buildGroupLevels(group.groups, tabsControlsContainer ?? tableInsideTabs);
    } else if (group.groups.length && this.service.arena.uiconfig.dataFormStyle === DataFormStyle.TABS) {
      await this.buildGroupLevels(group.groups, tabsControlsContainer ?? tableInsideTabs);
    }

    if (group.caption) {
      this.settings.groupList.push({ name: 'T' + groupIndex, caption: group.caption });
      this.settings.htmlGroups.push({
        cbxGroupDataSource: this.settings.groupList,
        html: tableInsideTabs,
      });

      let existingItem: Item | TabPanelItem;
      if (this.abcComponentUiCreator instanceof AbcAccordionUiCreator) {
        existingItem = this.settings.accordionDataSource.find((item) => item.title === group.caption);
      } else {
        existingItem = this.settings.tabsDataSource.find((item) => item.title === group.caption);
      }
      // critical for saving the table layout structure inside the accordion
      const wrapper = document.createElement('section');
      wrapper.style.height = '100%';
      wrapper.appendChild(tableInsideTabs);

      if (!existingItem && this.abcComponentUiCreator instanceof AbcAccordionUiCreator) {
        this.settings.pushToAcordionDataSource({
          title: group.caption,
          template: () => {
            return wrapper;
          },
          visible: true,
        });
      } else if (!existingItem && this.abcComponentUiCreator instanceof AbcTabsUiCreator) {
        this.settings.pushToTabsDataSource({
          title: group.caption,
          template: () => {
            return wrapper;
          },
          visible: true,
        });
      }
    } else {
      if (!nextGroup || nextGroup?.caption) {
        tabsElement.append(tableInsideTabs);
        parentContainer = undefined;
      } else {
        parentContainer = tableInsideTabs;
      }
    }

    if (this.settings.isSpecialTypeComment == true) {
      tableInsideTabs.style.height = '100%';
      tableInsideTabs.style.borderCollapse = 'collapse';
    }
    this.settings.isSpecialTypeComment = false;

    if (group?.tableLayoutSettings?.Columns) {
      tableInsideTabs.tBodies[0]?.insertAdjacentElement(
        'afterbegin',
        this.createEmptyRowWithColumns(group?.tableLayoutSettings?.Columns),
      );
    }

    return parentContainer;
  }

  private handlePDCComponents() {
    ['Properties', 'Documents', 'Comments'].forEach((identifier) => {
      let containerRef: ViewContainerRef;
      const targetComponent = this.settings.commonEditorsComponents.find((x) => x.name === identifier);
      const loadedComponent = this.service.arena.loadComponent(targetComponent.component, containerRef);

      const componentInstance = <PDC>loadedComponent.componentRef.instance;
      componentInstance.isReadOnly = this.settings.layoutViewModel.isReadOnly;

      if (componentInstance instanceof DocumentsEditorCardComponent) {
        componentInstance.isIconsVisible = true;
      }

      if (componentInstance instanceof AbcCommentsListComponent) {
        componentInstance.commentsCount.subscribe((isIncremented: boolean) => {
          this.service.arena.callUpdateActionsAndInfoDetails(isIncremented);
        });
      }

      this.refreshPDCdata(componentInstance, () => this.settings['is' + identifier + 'Checked']);

      this.settings.groupList.push({ name: identifier, caption: targetComponent.text });
      this.settings.htmlGroups.push({
        cbxGroupDataSource: this.settings.groupList,
        html: loadedComponent.domElement,
      });

      if (this.abcComponentUiCreator instanceof AbcTabsUiCreator) {
        const tabsTargetItem = this.settings.tabsDataSource.find((item) => item.title === targetComponent.text);
        if (!tabsTargetItem) {
          this.settings.tabsDataSource.push({
            title: targetComponent.text,
            text: identifier,
            template: () => {
              return loadedComponent.domElement;
            },
            visible: false,
          });
        } else {
          tabsTargetItem.template = () => {
            return loadedComponent.domElement;
          };
        }
      } else if (this.abcComponentUiCreator instanceof AbcAccordionUiCreator) {
        const accordionTargetItem = this.settings.accordionDataSource.find(
          (item) => item.title === targetComponent.text,
        );

        if (!accordionTargetItem) {
          this.settings.accordionDataSource.push({
            title: targetComponent.text,
            text: identifier,
            template: () => {
              return loadedComponent.domElement;
            },
            visible: false,
          });
        } else {
          accordionTargetItem.template = () => {
            return loadedComponent.domElement;
          };
        }
      }
    });
  }

  private handleUIConfiguration(tabsElement: HTMLElement) {
    const uiconfig: ArenaUIConfiguration = this.service.arena.uiconfig;
    if (uiconfig.renderDataForm && !uiconfig.renderHigna && !uiconfig.renderNavigation) {
      tabsElement.style.removeProperty('padding');
      tabsElement.style.removeProperty('box-shadow');
      tabsElement.classList.remove('dataFormTabs');
    }
  }

  private createTable(caption: string): HTMLTableElement {
    const table = document.createElement('table');

    table.id = this.service.arena.removeSpecialCharacter(caption ?? 'main');

    table.style.width = '100%';
    table.style.maxWidth = '100%';
    table.style.tableLayout = 'fixed';
    table.style.height = 'auto';
    table.style.borderSpacing = '0px';

    return table;
  }

  private async buildGroupLevels(groups: LayoutGroupViewModel[], parentContainer: HTMLElement): Promise<void> {
    let newContainer: HTMLElement;
    let htmlElement: HTMLElement;

    groups.forEach(async (group: LayoutGroupViewModel, index) => {
      if (group.layoutSettings) {
        newContainer = this.createLayoutStructure(parentContainer, group.layoutSettings);
      }
      if (group.caption) {
        let tableTemplate = this.createTable(group.caption);

        if (group.tableLayoutSettings) {
          this.createTableStructure(tableTemplate, group.tableLayoutSettings);
        }

        tableTemplate = await this.createControlsAndAppendToTable(
          tableTemplate,
          index,
          group.columns,
          group.subLayoutViews,
        );

        newContainer = tableTemplate;

        let dataSource: Item[] | GalleryModel[];
        if (
          this.service.arena.uiconfig.dataFormStyle === DataFormStyle.ACCORDION ||
          this.service.arena.uiconfig.dataFormStyle === DataFormStyle.TABS
        ) {
          dataSource = [{ title: group.caption, template: () => tableTemplate }];
        } else {
          const clone: any = {};
          clone.html = newContainer;
          clone.cbxGroupDataSource = groups.map((group: LayoutGroupViewModel) => {
            return group.caption;
          });
          dataSource = [clone];
        }

        htmlElement = await this.abcComponentUiCreator.create(dataSource);

        parentContainer.appendChild(htmlElement);
      }
      if (group.groups.length) {
        await this.buildGroupLevels(group.groups, newContainer);
      }
    });
  }

  private refreshPDCdata(pdcComponentInstance, isChecked: () => boolean) {
    this.settings.setCurrentObjectFunctions.push(async (object) => {
      if (isChecked()) {
        pdcComponentInstance.parentInfo = {
          id: object?.Id,
          objectType: object?.ObjectType,
        };
      }
    });
  }

  private createEmptyRowWithColumns(columns) {
    const tr: HTMLTableRowElement = document.createElement('tr');

    tr.className = 'layoutRow';

    for (let index = 0; index < columns.length; index++) {
      const column = columns[index];
      const td: HTMLTableCellElement = document.createElement('td');
      const sizeType = column.SizeType === SizeType.Percent ? '%' : 'px';
      td.style.width = <number>column.Width + sizeType;
      tr.appendChild(td);
    }

    return tr;
  }

  private async createControlsAndAppendToTable(
    table: HTMLTableElement,
    groupIndex: number,
    columns: LayoutColumnViewModel[],
    subLayoutViews: SubLayoutViewModel[],
    groups?: LayoutGroupViewModel[],
  ): Promise<HTMLTableElement> {
    if (columns?.length > 0) {
      for (const column of columns) {
        if (!column.isHiddenInDataForm) {
          const columnSettings = column.layoutSettings;
          const labelSettings = column.labelLayoutSettings;
          const td: HTMLTableCellElement = this.findTableCellById(table, column);
          if (td) {
            await this.checkOnDataTypeAndCreateControls(table, td, column);

            td.colSpan = <number>columnSettings?.ColumnSpan ?? <number>labelSettings.ColumnSpan;
            td.classList.add('tableCell');

            this.deleteCellsDependingOnColSpan(table, column, td);
            this.deleteCellsDependingOnRowSpan(table, column);
          } else {
            console.warn(columnSettings);
          }
        }
      }
    }

    if (subLayoutViews?.length > 0 && groupIndex > -1) {
      for (let i = 0; i < subLayoutViews.length; i++) {
        const slv: SubLayoutViewModel = subLayoutViews[i];
        const columnSettings = slv.layoutSettings;
        const td: HTMLTableCellElement = this.findTableCellById(table, slv);
        if (td) {
          td.colSpan = <number>columnSettings?.ColumnSpan ?? 1;
          td.classList.add('tableCell');
          this.deleteCellsDependingOnColSpan(table, slv, td);
          const rowHeight: string = table.rows[<number>columnSettings?.Row ?? 0].style.height;
          this.gridOnFormCreator._createGridOnForm(
            td,
            slv,
            rowHeight?.endsWith('%') && rowHeight !== '100%' ? 'auto' : rowHeight,
            groupIndex,
            i,
          );
        }
      }
    }
    if (this.areGroupsTabControlOnly()) {
      if (groups?.length > 0) {
        for (const group of groups) {
          const columnSettings = group.layoutSettings;
          const td: HTMLTableCellElement = this.findTableCellById(table, group);
          if (td) {
            // await this.checkOnDataTypeAndCreateControls(table, td, group);
            await this.buildGroupLevels(group.groups, td);
            td.colSpan = <number>columnSettings?.ColumnSpan;
            td.classList.add('tableCell');

            this.deleteCellsDependingOnColSpan(table, group, td);
            this.deleteCellsDependingOnRowSpan(table, group);
          } else {
            console.warn(columnSettings);
          }
        }
      }
    }

    return table;
  }

  private findTableCellById(
    table: HTMLTableElement,
    column: LayoutColumnViewModel | SubLayoutViewModel | LayoutGroupViewModel,
  ): HTMLTableCellElement {
    let td: HTMLTableCellElement;
    let labelLayoutSettings: LabelLayoutSettings;
    if (column instanceof LayoutColumnViewModel) {
      labelLayoutSettings = column?.labelLayoutSettings;
    }
    const layoutSettings: LayoutSettings = column?.layoutSettings;

    if (
      column instanceof LayoutColumnViewModel &&
      (column?.type === LayoutColumnType.GroupCaption || (!column.oPath && !column.specialType))
    ) {
      td = table.querySelector(
        '#' + table.id + '_Row_' + (labelLayoutSettings?.Row ?? 0) + '_Column_' + (labelLayoutSettings?.Column ?? 0),
      );
    } else {
      td = table.querySelector(
        '#' + table.id + '_Row_' + (layoutSettings?.Row ?? 0) + '_Column_' + (layoutSettings?.Column ?? 0),
      );
    }

    return td;
  }

  private deleteCellsDependingOnColSpan(
    table: HTMLTableElement,
    column: LayoutColumnViewModel | LayoutGroupViewModel | SubLayoutViewModel,
    td: HTMLTableCellElement,
  ): void {
    const layoutSettings: LayoutSettings = column?.layoutSettings;
    const labelLayoutSettings: LabelLayoutSettings = (<LayoutColumnViewModel>column)?.labelLayoutSettings;
    let rowIndex: number = 0;
    let colSpan: number = 1;
    let columnIndex: number = 0;

    rowIndex = <number>layoutSettings?.Row ?? <number>labelLayoutSettings?.Row ?? 0;
    colSpan = <number>layoutSettings?.ColumnSpan ?? <number>labelLayoutSettings?.ColumnSpan ?? 1;
    columnIndex = <number>layoutSettings?.Column ?? <number>labelLayoutSettings?.Column ?? 0;

    if (colSpan > 1) {
      const row = td.parentElement;
      for (let i = columnIndex + 1; i < columnIndex + colSpan; i++) {
        row?.querySelector('#' + table.id + '_Row_' + rowIndex + '_Column_' + i)?.remove();
      }
    }
  }

  private deleteCellsDependingOnRowSpan(
    table: HTMLTableElement,
    column: LayoutColumnViewModel | LayoutGroupViewModel | SubLayoutViewModel,
  ): void {
    const layoutSettings: LayoutSettings = column?.layoutSettings;
    const rowSpan: number = <number>layoutSettings?.RowSpan ?? 1;
    const rowPosition: number = <number>layoutSettings?.Row ?? 0;
    const columnPosition: number = <number>layoutSettings?.Column ?? 0;
    const columnSpan: number = <number>layoutSettings?.ColumnSpan ?? 0;

    if (rowSpan > 1) {
      const affectedRows = rowSpan + rowPosition;
      for (let siblingRow = rowPosition + 1; siblingRow < affectedRows; siblingRow++) {
        const affectedCols = columnPosition + columnSpan;
        for (let c = columnPosition; c < affectedCols; c++) {
          table.rows[siblingRow]?.querySelector('#' + table.id + '_Row_' + siblingRow + '_Column_' + c)?.remove();
        }
      }
    }
  }

  private createTableStructure(table: HTMLTableElement, tableLayoutSettings: TableLayoutSettings): HTMLTableElement {
    for (let i = 0; i < tableLayoutSettings?.Rows?.length; i++) {
      const row = tableLayoutSettings.Rows[i];
      const tr = table.insertRow(i);
      const height: string = this.getSizeAndSizeTypeAsString(<number>row.Height, row.SizeType);

      if (height) {
        tr.style.height = height;
      }

      for (let j = 0; j < tableLayoutSettings.Columns?.length; j++) {
        const column = tableLayoutSettings.Columns[j];
        const td = tr.insertCell(j);
        const width: string = this.getSizeAndSizeTypeAsString(<number>column.Width, column.SizeType);

        if (width) {
          td.style.width = width;
        }

        td.id = table.id + '_Row_' + i + '_Column_' + j;
      }
    }
    return table;
  }

  private createLayoutStructure(container: HTMLElement, layoutSettings: LayoutSettings): HTMLTableCellElement {
    const tr = container['rows'][layoutSettings.Row ?? 0];
    const td = tr['cells'][layoutSettings.Column ?? 0];
    td.colSpan = layoutSettings.ColumnSpan;
    td.rowSpan = layoutSettings.RowSpan;

    return td;
  }

  private changeDefaultCellsHeight(table: HTMLTableElement): void {
    const rows = table.rows;
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      const cells = row.cells;
      let isEmptyRow = true;
      for (let j = 0; j < cells?.length; j++) {
        const cell = cells[j];
        if (cell.hasChildNodes()) {
          isEmptyRow = false;
        }
      }

      if (isEmptyRow) {
        const maxHeightOfEmptyCells: number = StringHelper.extractNumber(
          this.service.config.configuration.Style?.DataForm?.MaxHeightOfEmptyRows,
        );
        const rowHeight = StringHelper.extractNumber(row.style.height);
        const isPercentage = row.style.height?.includes('%');
        if (maxHeightOfEmptyCells > -1) {
          if (maxHeightOfEmptyCells < rowHeight && !isPercentage) {
            row.style.height = this.service.config.configuration.Style?.DataForm?.MaxHeightOfEmptyRows;
          }
        }
        // else {
        //   if (i != 0 && !isPercentage) {
        //     table.deleteRow(i);
        //   }
        // }
      }
    }
  }

  private getSizeAndSizeTypeAsString(size: number, sizeType: SizeType): string {
    if (size) {
      return size + (sizeType === SizeType.Absolute ? 'px' : '%');
    }
    return null;
  }

  private async checkOnDataTypeAndCreateControls(
    table: HTMLTableElement,
    td: HTMLTableCellElement,
    column: LayoutColumnViewModel,
    ignoreSpecialType?: boolean,
  ): Promise<HTMLTableCellElement> {
    let uiComponent: UIComponent<AbcComponent>;
    if (!column.isHiddenInDataForm) {
      const opath = column.oPath?.replace('ref:', '');
      // const isComplexOpath = opath?.includes('.') || false;
      //CHE: Changeset 71165 (Check With Markus) changed with isComplexOpath!
      // this.settings.mainObjectTypeViewModel.columns[column.oPath?.replace('ref:','')]
      if (!column.isReadOnly && !column?.oPath?.startsWith('Properties[')) {
        if (
          !this.isColumnInOT(this.settings.mainObjectTypeViewModel, opath) &&
          column?.specialType !== SpecialType.Comment
        ) {
          column.isReadOnly = true;
        }
      }
      if (column.isRef && (column.specialType !== SpecialType.Property || ignoreSpecialType)) {
        uiComponent = await this.standardUiCreator.createAbcDropDownBox(td, column);
      } else if (column.type && column.type === LayoutColumnType.GroupCaption) {
        td.insertAdjacentElement('afterbegin', this.createGroupCaption(td, column));
      } else if (!column.specialType || ignoreSpecialType) {
        if (column.dataType) {
          if (!column.oPath) {
            if (column.caption) {
              td.insertAdjacentElement('afterbegin', this.createLabel(td, column));
            }
          } else {
            switch (column.dataType) {
              case 'boolean':
                uiComponent = this.standardUiCreator.createAbcCheckbox(td, column);
                break;
              case 'date':
              case 'datetime':
                uiComponent = this.standardUiCreator.createAbcDateBox(
                  td,
                  column,
                  column.dataType === 'datetime' ? DateBoxType.Time : null,
                );
                break;
              case 'string':
                uiComponent = this.standardUiCreator.createAbcTextBox(td, column);
                break;
              case 'number':
                uiComponent = this.standardUiCreator.createAbcNumberBox(td, column);
                break;
              default:
                uiComponent = this.standardUiCreator.createAbcTextBox(td, column);
                break;
            }
          }
        } else if (!column?.oPath && column?.caption && column?.labelLayoutSettings) {
          const span = document.createElement('span');
          span.appendChild(document.createTextNode(column.caption));
          td.classList.add('tdCaptionOnly');
          td.insertAdjacentElement('afterbegin', span);
        } else {
          uiComponent = this.standardUiCreator.createAbcTextBox(td, column);
        }
      } else if (column.specialType) {
        switch (column.specialType) {
          case SpecialType.CommentsEditor:
            if (!column.oPath && column.caption && column.labelLayoutSettings) {
              const labelLayoutSetting = column.labelLayoutSettings;
              const span = document.createElement('span');
              span.appendChild(document.createTextNode(column.caption));
              const labelTd = table.querySelector(
                '#' +
                  table.id +
                  '_Row_' +
                  (labelLayoutSetting.Row ?? 0) +
                  '_Column_' +
                  (labelLayoutSetting.Column ?? 0),
              );
              labelTd.classList.add('tdCaptionOnly');
              labelTd.insertAdjacentElement('afterbegin', span);
            }
            uiComponent = this.specialTypeCreator.createCommentsEditor(td, column);
            break;
          case SpecialType.Comment:
            this.settings.isSpecialTypeComment = true;
            if (column.isEditableComment) {
              uiComponent = this.specialTypeCreator.createAbcHtmlEditor(td, column);
            } else {
              uiComponent = this.specialTypeCreator.createAbcTextArea(td, column);
            }
            break;
          case SpecialType.Document:
            uiComponent = this.specialTypeCreator.createAbcDocumentInput(td, column);
            break;
          case SpecialType.LayoutReferenceId:
            // TODO Add AbcCombobox here
            break;
          case SpecialType.Property:
            const propColumnName = column.oPath?.match(/\'(.*?)\'/)[1];
            const map = this.service.properties.propertiesVM?.namedPropertiesByOT;
            if (propColumnName && map) {
              let ot: ObjectTypeInfoViewModel = this.settings.mainObjectTypeViewModel;
              let p: { type: PropertyType; picklist: string } = undefined;
              while (ot && !p) {
                if (map[ot.name]) {
                  p = map[ot.name][propColumnName];
                }
                if (!p) {
                  ot = ot.baseType;
                }
              }
              if (p) {
                const type: PropertyType = p.type;
                column.dataType = this.specialTypeCreator.getPropertyType(type);
                if (column.dataType === 'number' && !column.formatSettings?.EditMask) {
                  if (!column.formatSettings) {
                    column.formatSettings = new FormatSettings();
                  }
                  column.formatSettings.EditMask = '########0.###';
                }
                if ((type === PropertyType.Picklist || type === PropertyType.ListOfValues) && p?.picklist) {
                  uiComponent = await this.standardUiCreator.createAbcDropDownBox(td, column, p?.picklist);
                  break;
                }
              }
            }
            await this.checkOnDataTypeAndCreateControls(table, td, column, true);
            break;
          case SpecialType.ObjectTypeDesignation:
            uiComponent = this.standardUiCreator.createAbcTextBox(td, column);
            break;
          case SpecialType.OPathEditor:
            uiComponent = this.standardUiCreator.createAbcTextBox(td, column);
            break;
          case SpecialType.PropertiesEditor:
            uiComponent = this.specialTypeCreator.createPropertiesEditor(td, column);
            break;
          case SpecialType.LinkLabel:
          case SpecialType.LinkLabelEmail:
          case SpecialType.LinkLabelUrl:
            uiComponent = this.standardUiCreator.createLinkLabel(td, column);
            break;
          case SpecialType.DocumentsEditor:
            uiComponent = this.specialTypeCreator.createDocumentsEditor(td, column);
            break;
          default:
            break;
        }
      }
      if (!CompareHelper.isEmptyObject(uiComponent)) {
        td.insertAdjacentElement('afterbegin', uiComponent.domElement);
        column.layoutColumnObject.Control = uiComponent.componentInstance;
      }
    }
    return td;
  }

  private isColumnInOT(otvm: ObjectTypeInfoViewModel, opath: string): boolean {
    if (otvm.columnsByName.has(opath)) {
      return true;
    } else {
      if (otvm.baseType) {
        return this.isColumnInOT(otvm.baseType, opath);
      } else {
        return false;
      }
    }
  }
}
